/**
 * PortalPaymentSection.js
 * Patient portal payment component — credit/debit card payments via Stripe.
 * Loads Stripe.js dynamically, renders card element, handles PaymentIntent flow,
 * and offers email/SMS receipt delivery.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  CreditCard, DollarSign, Mail, Phone, CheckCircle,
  AlertCircle, Loader, Receipt, ChevronDown, ChevronUp, RefreshCw,
} from 'lucide-react';
import { NEU } from '../neuStyles';

// ─── Load Stripe.js from CDN ────────────────────────────────────────────────
let stripePromise = null;
function loadStripe(publishableKey) {
  if (stripePromise) return stripePromise;
  stripePromise = new Promise((resolve, reject) => {
    if (window.Stripe) {
      resolve(window.Stripe(publishableKey));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => {
      if (window.Stripe) resolve(window.Stripe(publishableKey));
      else reject(new Error('Stripe.js failed to initialize'));
    };
    script.onerror = () => reject(new Error('Failed to load Stripe.js'));
    document.head.appendChild(script);
  });
  return stripePromise;
}

// ─── Formatting helpers ─────────────────────────────────────────────────────
function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
}
function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// ─── Styles ─────────────────────────────────────────────────────────────────
const styles = {
  wrapper: {
    ...NEU.card,
    marginBottom: '1rem',
    overflow: 'hidden',
  },
  header: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '1rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
  },
  headerTitle: {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    fontWeight: 700, fontSize: '1rem', color: '#111827',
  },
  content: { padding: '0 1.25rem 1.25rem' },
  invoiceRow: {
    ...NEU.cardSm,
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0.75rem 1rem', marginBottom: '0.5rem',
  },
  label: {
    display: 'block', fontWeight: 600, fontSize: '0.8rem', color: '#374151',
    marginBottom: '0.3rem',
  },
  cardElementBox: {
    ...NEU.inset,
    padding: '12px 14px',
    minHeight: 22,
  },
  amountInput: {
    ...NEU.input,
    width: '100%', padding: '0.6rem 0.875rem', fontSize: '1.1rem',
    boxSizing: 'border-box', fontWeight: 600,
  },
  payBtn: {
    ...NEU.btnPrimary,
    width: '100%', padding: '0.875rem', fontSize: '1rem',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
    marginTop: '1rem',
  },
  payBtnDisabled: {
    opacity: 0.6, cursor: 'not-allowed',
  },
  successBox: {
    ...NEU.card,
    padding: '2rem', textAlign: 'center',
  },
  errorMsg: {
    display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
    padding: '0.75rem 1rem', color: '#991b1b', fontSize: '0.85rem', marginTop: '0.75rem',
  },
  checkbox: {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    fontSize: '0.85rem', color: '#374151', cursor: 'pointer',
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
//  Main Component
// ═══════════════════════════════════════════════════════════════════════════════

export default function PortalPaymentSection({ sessionToken }) {
  const [open, setOpen]               = useState(true);
  const [loading, setLoading]         = useState(true);
  const [balanceData, setBalanceData] = useState(null);
  const [error, setError]             = useState('');

  // Payment state
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [customAmount, setCustomAmount]         = useState('');
  const [paying, setPaying]                     = useState(false);
  const [payError, setPayError]                 = useState('');

  // Stripe
  const [stripe, setStripe]             = useState(null);
  const [cardElement, setCardElement]   = useState(null);
  const [cardReady, setCardReady]       = useState(false);
  const [cardError, setCardError]       = useState('');
  const cardMountRef                    = useRef(null);
  const elementsRef                     = useRef(null);

  // Success / Receipt state
  const [paymentResult, setPaymentResult] = useState(null);
  const [receiptEmail, setReceiptEmail]   = useState(true);
  const [receiptSMS, setReceiptSMS]       = useState(false);
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const [receiptSent, setReceiptSent]       = useState(false);
  const [receiptError, setReceiptError]     = useState('');

  // ── Load balance ────────────────────────────────────────────────────────────
  const fetchBalance = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await window.zignagenixAPI.portalPayBalance(sessionToken);
      if (data?.error) {
        setError(data.message);
      } else {
        setBalanceData(data);
        // Pre-select all invoices
        if (data?.invoices?.length) {
          setSelectedInvoices(data.invoices.map(i => i.id));
          setCustomAmount(data.totalBalance.toFixed(2));
        }
      }
    } catch (err) {
      setError('Failed to load billing information.');
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  // ── Load Stripe + mount card element ────────────────────────────────────────
  useEffect(() => {
    if (!balanceData || balanceData.totalBalance <= 0) return;

    let mounted = true;
    (async () => {
      try {
        const keyRes = await window.zignagenixAPI.portalPayPublishableKey();
        if (!keyRes?.key) {
          setError('Payment system not configured. Please contact the clinic.');
          return;
        }
        const stripeInstance = await loadStripe(keyRes.key);
        if (!mounted) return;
        setStripe(stripeInstance);

        // Create elements
        const elements = stripeInstance.elements();
        elementsRef.current = elements;
        const card = elements.create('card', {
          style: {
            base: {
              fontSize: '16px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              color: '#1f2937',
              '::placeholder': { color: '#9ca3af' },
            },
            invalid: { color: '#dc2626' },
          },
          hidePostalCode: false,
        });
        setCardElement(card);

        // Mount when ref ready
        const waitForMount = () => {
          if (cardMountRef.current) {
            card.mount(cardMountRef.current);
            card.on('ready', () => { if (mounted) setCardReady(true); });
            card.on('change', (event) => {
              if (mounted) setCardError(event.error ? event.error.message : '');
            });
          } else {
            setTimeout(waitForMount, 100);
          }
        };
        waitForMount();
      } catch (err) {
        if (mounted) setError('Failed to load payment form. Please try again.');
      }
    })();

    return () => { mounted = false; };
  }, [balanceData]);

  // ── Toggle invoice selection ────────────────────────────────────────────────
  function toggleInvoice(invoiceId) {
    setSelectedInvoices(prev => {
      const next = prev.includes(invoiceId)
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId];
      // Recalculate amount
      if (balanceData?.invoices) {
        const total = balanceData.invoices
          .filter(inv => next.includes(inv.id))
          .reduce((sum, inv) => sum + inv.balance, 0);
        setCustomAmount(total.toFixed(2));
      }
      return next;
    });
  }

  function selectAllInvoices() {
    if (!balanceData?.invoices) return;
    setSelectedInvoices(balanceData.invoices.map(i => i.id));
    setCustomAmount(balanceData.totalBalance.toFixed(2));
  }

  // ── Handle payment ─────────────────────────────────────────────────────────
  async function handlePay() {
    setPayError('');
    const amount = parseFloat(customAmount);
    if (isNaN(amount) || amount < 0.50) {
      setPayError('Minimum payment amount is $0.50.');
      return;
    }
    if (!stripe || !cardElement) {
      setPayError('Payment form not loaded. Please wait or refresh.');
      return;
    }

    setPaying(true);
    try {
      const amountCents = Math.round(amount * 100);

      // 1) Create PaymentIntent
      const intentRes = await window.zignagenixAPI.portalPayCreateIntent(
        sessionToken, amountCents, selectedInvoices
      );
      if (intentRes?.error) {
        setPayError(intentRes.message);
        setPaying(false);
        return;
      }

      // 2) Confirm with Stripe.js
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        intentRes.clientSecret,
        { payment_method: { card: cardElement } }
      );

      if (stripeError) {
        setPayError(stripeError.message);
        setPaying(false);
        return;
      }

      if (paymentIntent.status !== 'succeeded') {
        setPayError(`Payment status: ${paymentIntent.status}. Please try again.`);
        setPaying(false);
        return;
      }

      // 3) Record locally
      const confirmRes = await window.zignagenixAPI.portalPayConfirm(
        sessionToken, paymentIntent.id, selectedInvoices
      );
      if (confirmRes?.error) {
        // Payment went through on Stripe but local recording failed — still show success
        console.error('Local confirm failed:', confirmRes.message);
      }

      setPaymentResult({
        amount,
        paymentIntentId: paymentIntent.id,
        last4: confirmRes?.last4 || '',
        brand: confirmRes?.brand || '',
        patientEmail: balanceData?.patientEmail || '',
        patientPhone: balanceData?.patientPhone || '',
      });

    } catch (err) {
      setPayError(err.message || 'Payment failed. Please try again.');
    } finally {
      setPaying(false);
    }
  }

  // ── Send receipt ───────────────────────────────────────────────────────────
  async function handleSendReceipt() {
    if (!paymentResult) return;
    setSendingReceipt(true);
    setReceiptError('');
    try {
      const res = await window.zignagenixAPI.portalPaySendReceipt(sessionToken, {
        amount: paymentResult.amount,
        paymentIntentId: paymentResult.paymentIntentId,
        last4: paymentResult.last4,
        brand: paymentResult.brand,
        sendEmail: receiptEmail,
        sendSMS: receiptSMS,
        email: paymentResult.patientEmail,
        phone: paymentResult.patientPhone,
      });
      if (res?.error) {
        setReceiptError(res.message);
      } else {
        setReceiptSent(true);
        // Check individual results
        const issues = [];
        if (receiptEmail && res?.results?.email && !res.results.email.success) {
          issues.push('Email: ' + (res.results.email.error || 'failed'));
        }
        if (receiptSMS && res?.results?.sms && !res.results.sms.success) {
          issues.push('SMS: ' + (res.results.sms.error || 'failed'));
        }
        if (issues.length) setReceiptError('Partial: ' + issues.join('; '));
      }
    } catch (err) {
      setReceiptError('Failed to send receipt.');
    } finally {
      setSendingReceipt(false);
    }
  }

  // ── Reset after payment ────────────────────────────────────────────────────
  function handleNewPayment() {
    setPaymentResult(null);
    setReceiptSent(false);
    setReceiptError('');
    setPayError('');
    stripePromise = null; // Reset so new key can be loaded
    fetchBalance();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  // ── Success Screen ──
  if (paymentResult) {
    const cardLabel = paymentResult.brand
      ? `${paymentResult.brand.charAt(0).toUpperCase() + paymentResult.brand.slice(1)} ••••${paymentResult.last4}`
      : `Card ••••${paymentResult.last4}`;

    return (
      <div style={styles.wrapper}>
        <button onClick={() => setOpen(o => !o)} style={styles.header}>
          <span style={styles.headerTitle}>
            <CreditCard size={18} color="#059669" /> Payment Successful
          </span>
          {open ? <ChevronUp size={18} color="#6C757D" /> : <ChevronDown size={18} color="#6C757D" />}
        </button>
        {open && (
          <div style={styles.content}>
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <CheckCircle size={48} color="#059669" style={{ marginBottom: '0.75rem' }} />
              <h3 style={{ margin: '0 0 0.25rem', color: '#059669', fontSize: '1.5rem' }}>
                {formatCurrency(paymentResult.amount)}
              </h3>
              <p style={{ margin: 0, color: '#6C757D', fontSize: '0.85rem' }}>
                Paid with {cardLabel}
              </p>
              <p style={{ margin: '0.25rem 0 0', color: '#9CA3AF', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                Ref: {paymentResult.paymentIntentId}
              </p>
            </div>

            {/* Receipt options */}
            <div style={{ ...NEU.cardSm, padding: '1rem', marginTop: '1rem' }}>
              <p style={{ margin: '0 0 0.75rem', fontWeight: 700, fontSize: '0.9rem', color: '#111827', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Receipt size={16} color="#2E8B98" /> Send Receipt
              </p>

              {!receiptSent ? (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    {paymentResult.patientEmail && (
                      <label style={styles.checkbox}>
                        <input
                          type="checkbox"
                          checked={receiptEmail}
                          onChange={e => setReceiptEmail(e.target.checked)}
                        />
                        <Mail size={14} color="#6C757D" />
                        Email to {paymentResult.patientEmail}
                      </label>
                    )}
                    {paymentResult.patientPhone && (
                      <label style={styles.checkbox}>
                        <input
                          type="checkbox"
                          checked={receiptSMS}
                          onChange={e => setReceiptSMS(e.target.checked)}
                        />
                        <Phone size={14} color="#6C757D" />
                        Text to {paymentResult.patientPhone}
                      </label>
                    )}
                    {!paymentResult.patientEmail && !paymentResult.patientPhone && (
                      <p style={{ color: '#6C757D', fontSize: '0.85rem', margin: 0 }}>
                        No email or phone number on file. Please contact the clinic for a receipt.
                      </p>
                    )}
                  </div>

                  {(receiptEmail || receiptSMS) && (
                    <button
                      onClick={handleSendReceipt}
                      disabled={sendingReceipt}
                      style={{
                        ...NEU.btnPrimary,
                        padding: '0.5rem 1.25rem', fontSize: '0.85rem',
                        display: 'flex', alignItems: 'center', gap: '0.4rem',
                        opacity: sendingReceipt ? 0.6 : 1,
                      }}
                    >
                      {sendingReceipt ? <Loader size={14} className="spin" /> : <Mail size={14} />}
                      {sendingReceipt ? 'Sending…' : 'Send Receipt'}
                    </button>
                  )}
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#059669', fontSize: '0.85rem' }}>
                  <CheckCircle size={16} /> Receipt sent successfully
                </div>
              )}

              {receiptError && (
                <div style={styles.errorMsg}>
                  <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>{receiptError}</span>
                </div>
              )}
            </div>

            <button
              onClick={handleNewPayment}
              style={{
                ...NEU.btnGhost,
                width: '100%', padding: '0.625rem', fontSize: '0.85rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                marginTop: '1rem',
              }}
            >
              <RefreshCw size={14} /> Make Another Payment
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Main Payment Form ──
  return (
    <div style={styles.wrapper}>
      <button onClick={() => setOpen(o => !o)} style={styles.header}>
        <span style={styles.headerTitle}>
          <DollarSign size={18} color="#2E8B98" /> Make a Payment
        </span>
        {open ? <ChevronUp size={18} color="#6C757D" /> : <ChevronDown size={18} color="#6C757D" />}
      </button>

      {open && (
        <div style={styles.content}>
          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: '#6C757D' }}>
              <Loader size={24} className="spin" style={{ marginBottom: '0.5rem' }} />
              <p style={{ margin: 0, fontSize: '0.85rem' }}>Loading billing information…</p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div style={styles.errorMsg}>
              <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{error}</span>
            </div>
          )}

          {/* No balance */}
          {!loading && !error && balanceData && balanceData.totalBalance <= 0 && (
            <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
              <CheckCircle size={32} color="#059669" style={{ marginBottom: '0.5rem' }} />
              <p style={{ margin: 0, fontWeight: 600, color: '#059669', fontSize: '1rem' }}>
                No Outstanding Balance
              </p>
              <p style={{ margin: '0.25rem 0 0', color: '#6C757D', fontSize: '0.85rem' }}>
                All invoices are paid. Thank you!
              </p>
            </div>
          )}

          {/* Has balance — show payment form */}
          {!loading && !error && balanceData && balanceData.totalBalance > 0 && (
            <>
              {/* Total balance banner */}
              <div style={{
                ...NEU.inset, padding: '1rem', marginBottom: '1rem',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>
                  Total Outstanding Balance
                </span>
                <span style={{ fontWeight: 700, fontSize: '1.25rem', color: '#dc2626' }}>
                  {formatCurrency(balanceData.totalBalance)}
                </span>
              </div>

              {/* Invoice list */}
              {balanceData.invoices?.length > 0 && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>
                      Select Invoices to Pay
                    </span>
                    <button
                      onClick={selectAllInvoices}
                      style={{
                        background: 'none', border: 'none', color: '#2E8B98',
                        cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline',
                      }}
                    >
                      Select All
                    </button>
                  </div>

                  {balanceData.invoices.map(inv => (
                    <label
                      key={inv.id}
                      style={{
                        ...styles.invoiceRow,
                        cursor: 'pointer',
                        background: selectedInvoices.includes(inv.id) ? '#f0fdf4' : NEU.bg,
                        transition: 'background 0.15s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <input
                          type="checkbox"
                          checked={selectedInvoices.includes(inv.id)}
                          onChange={() => toggleInvoice(inv.id)}
                          style={{ width: 16, height: 16, accentColor: '#2E8B98' }}
                        />
                        <div>
                          <p style={{ margin: 0, fontWeight: 600, fontSize: '0.875rem', color: '#111827' }}>
                            #{inv.invoiceNumber}
                          </p>
                          <p style={{ margin: 0, fontSize: '0.75rem', color: '#6C757D' }}>
                            {formatDate(inv.date)}{inv.dueDate ? ` · Due ${formatDate(inv.dueDate)}` : ''}
                          </p>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
                          {formatCurrency(inv.balance)}
                        </p>
                        {inv.amountPaid > 0 && (
                          <p style={{ margin: 0, fontSize: '0.7rem', color: '#059669' }}>
                            {formatCurrency(inv.amountPaid)} paid
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {/* Amount */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={styles.label}>Payment Amount</label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                    fontWeight: 700, color: '#6C757D', fontSize: '1.1rem',
                  }}>$</span>
                  <input
                    type="number"
                    min="0.50"
                    step="0.01"
                    value={customAmount}
                    onChange={e => setCustomAmount(e.target.value)}
                    style={{ ...styles.amountInput, paddingLeft: '1.75rem' }}
                    onFocus={e => e.target.style.boxShadow = NEU.shadow.sm}
                    onBlur={e => e.target.style.boxShadow = NEU.shadow.insetSm}
                  />
                </div>
              </div>

              {/* Card Element */}
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={styles.label}>
                  <CreditCard size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                  Card Details
                </label>
                <div style={styles.cardElementBox} ref={cardMountRef} />
                {cardError && (
                  <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.3rem 0 0' }}>{cardError}</p>
                )}
              </div>

              {/* Pay error */}
              {payError && (
                <div style={styles.errorMsg}>
                  <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span>{payError}</span>
                </div>
              )}

              {/* Pay button */}
              <button
                onClick={handlePay}
                disabled={paying || !cardReady}
                style={{
                  ...styles.payBtn,
                  ...(paying || !cardReady ? styles.payBtnDisabled : {}),
                }}
              >
                {paying ? (
                  <>
                    <Loader size={18} className="spin" />
                    Processing…
                  </>
                ) : (
                  <>
                    <CreditCard size={18} />
                    Pay {customAmount ? formatCurrency(parseFloat(customAmount) || 0) : ''}
                  </>
                )}
              </button>

              <p style={{ margin: '0.75rem 0 0', color: '#9CA3AF', fontSize: '0.75rem', textAlign: 'center' }}>
                Payments processed securely via Stripe. Your card details never touch our servers.
              </p>
            </>
          )}
        </div>
      )}

      {/* Spinner animation */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
